import { useTranslation } from 'react-i18next';
import { useCreation } from 'ahooks';
import {
  type ColorSystemOptions,
  type SupportedColorScheme,
  type ThemeOptions,
  alpha,
  createTheme,
  useMediaQuery,
} from '@mui/material';
import { blue, common, emerald, grey, orange, red, slate, zinc } from '@mui/material/colors';

declare module '@mui/material/styles' {
  interface Mixins {
    headerPlaceholder: React.CSSProperties | Record<string, any>;
  }
  interface TypographyVariants {
    pageTitle: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    pageTitle?: React.CSSProperties | Record<string, any>;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    pageTitle: true;
  }
}

function getFontFamily() {
  return `system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "PingFang SC", "Microsoft YaHei", sans-serif;`;
}

const breakpoints = {
  values: {
    xs: 0,
    sm: 560,
    md: 960,
    lg: 1200,
    xl: 1600,
  },
};

const baseThemeConfig: ThemeOptions & { cssVariables: boolean } = {
  breakpoints,
  mixins: {
    headerPlaceholder: {
      height: '2.25rem',
      [`@media (min-width:${breakpoints.values.md}px)`]: {
        height: '2.625rem',
      },
      [`@media (min-width:${breakpoints.values.lg}px)`]: {
        height: '3rem',
      },
    },
  },
  cssVariables: true,
  components: {
    MuiCssBaseline: {
      styleOverrides: (theme) => {
        return {
          html: {
            scrollBehavior: 'smooth',
            maxHeight: '100dvh',
            fontSize: '1rem',
            background: `${theme.vars?.palette.background.default} !important`,
            [theme.breakpoints.down('lg')]: {
              fontSize: '0.9rem',
            },
            [theme.breakpoints.down('sm')]: {
              fontSize: '0.85rem',
              background: `${theme.vars?.palette.background.paper} !important`,
            },
          },
          body: {
            background: theme.vars?.palette.background.default,
            height: '100%',
            [theme.breakpoints.down('sm')]: {
              background: theme.vars?.palette.background.paper,
            },
          },
          '#root': {
            height: '100%',
            [theme.breakpoints.down('sm')]: {
              background: theme.vars?.palette.background.paper,
            },
          },
          '.MuiOutlinedInput-notchedOutline': {
            '@media (forced-colors: active)': {
              borderColor: 'buttonText !important',
            },
          },
        };
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          '@media (forced-colors: active)': {
            ':not(:has(.MuiCardActionArea-root))': {
              border: '1px solid',
            },
          },
          borderRadius: 8,
        },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: {
          '@media (forced-colors: active)': {
            '&:hover': {
              borderColor: 'Highlight',
              color: 'Highlight',
            },
            '.MuiSvgIcon-root': {
              color: 'unset',
            },
            border: '1px solid ButtonBorder',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          border: '2px solid #fff',
          borderRadius: '64px',
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        PaperProps: {
          sx: {
            borderRadius: '16px',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          transition: 'unset',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          fontFamily: 'inherit',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-focusVisible': {
            outline: `2px solid ${theme.vars?.palette.action.focusRing}`,
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: ({ theme }) => ({
          borderColor: slate[500],
          ...theme.applyStyles('dark', {
            borderColor: zinc[500],
          }),
        }),
        root: ({ theme }) => ({
          [`&.Mui-focused .MuiOutlinedInput-notchedOutline`]: {
            borderColor: blue[700],
          },
          [`& .MuiOutlinedInput-input:-webkit-autofill`]: {
            WebkitBoxShadow: `0 0 0 1000px ${theme.vars?.palette.background.default} inset !important`,
            WebkitTextFillColor: theme.vars?.palette.text.primary,
          },
          ...theme.applyStyles('dark', {
            [`&.Mui-focused .MuiOutlinedInput-notchedOutline`]: {
              borderColor: blue[500],
            },
          }),
        }),
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        fontSizeSmall: ({ theme }) => ({
          fontSize: theme.typography.body1.fontSize,
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-focusVisible': {
            outline: `2px solid ${theme.vars?.palette.action.focusRing}`,
            outlineOffset: '-2px',
          },
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:hover': {
            backgroundColor: common.white,
          },
          backgroundColor: slate[100],
          ...theme.applyStyles('dark', {
            '&:hover': {
              backgroundColor: common.black,
            },
            backgroundColor: zinc[900],
          }),
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          border: `1px solid ${theme.vars?.palette.border}`,
          borderRadius: '8px',
          padding: '12px',
        }),
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          body1: 'p',
          body2: 'p',
          h1: 'span',
          h2: 'span',
          h3: 'span',
          h4: 'span',
          h5: 'span',
          h6: 'span',
          inherit: 'p',
          label1: 'p',
          label2: 'p',
          subtitle1: 'span',
          subtitle2: 'span',
          pageTitle: 'span',
        },
      },
      styleOverrides: {
        root: ({ ownerState }) => {
          const style = {
            '@media (forced-colors: active)': {
              color: 'unset',
            },
          };
          if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'pageTitle'].includes(ownerState.variant ?? '')) {
            return {
              ...style,
              [`&.MuiTypography-${ownerState.variant}`]: {
                display: 'block',
              },
            };
          }
          return style;
        },
      },
    },

    // iecf customize
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          //fontSize: '0.875rem',
          fontWeight: 600,
        },
      },
    },
  },
  typography: {
    button: {
      textTransform: 'none',
    },
    fontFamily: getFontFamily(),
    pageTitle: {
      fontSize: '1.7rem',
      lineHeight: '2.125rem',
      [`@media (min-width:${breakpoints.values.sm}px)`]: {
        fontSize: '1.8rem',
        lineHeight: '2.25rem',
      },
      [`@media (min-width:${breakpoints.values.md}px)`]: {
        fontSize: '2rem',
        lineHeight: '2.5rem',
      },
    },
  },
};

const lightThemeColorSchemes: Partial<Record<SupportedColorScheme, ColorSystemOptions>> = {
  light: {
    palette: {
      action: {
        focusRing: blue[700],
      },
      Alert: {
        errorColor: slate[900],
        infoColor: slate[900],
        successColor: slate[900],
        warningColor: slate[900],
      },
      background: {
        contrast: slate[900],
        default: slate[50],
        input: slate[100],
        paper: common.white,
      },
      border: slate[300],
      contrastThreshold: 4.5,
      divider: slate[300],
      error: {
        contrastText: common.white,
        dark: red[800],
        light: red[50],
        main: red[700],
      },
      info: {
        dark: blue[800],
        light: blue[50],
        main: blue[700],
      },
      mode: 'light',
      primary: {
        contrastText: common.white,
        dark: blue[800],
        light: blue[100],
        main: blue[700],
      },
      secondary: {
        contrastText: slate[900],
        dark: slate[400],
        light: slate[100],
        main: slate[300],
      },
      success: {
        dark: emerald[800],
        light: emerald[50],
        main: emerald[700],
      },
      text: {
        disabled: grey[500],
        primary: slate[900],
        secondary: slate[700],
      },
      Tooltip: {
        bg: slate[900],
      },
      warning: {
        dark: orange[800],
        light: orange[50],
        main: orange[700],
      },
    },
  },
  dark: {
    palette: {
      mode: 'dark',
      action: {
        focusRing: blue[500],
      },
      Alert: {
        errorColor: zinc[300],
        infoColor: zinc[300],
        successColor: zinc[300],
        warningColor: zinc[300],
      },
      background: {
        contrast: slate[200],
        default: common.black,
        input: zinc[900],
        paper: zinc[900],
      },
      border: zinc[600],
      contrastThreshold: 4.5,
      divider: zinc[600],
      error: {
        contrastText: zinc[900],
        dark: red[300],
        light: '#2F0808',
        main: red[400],
      },
      info: {
        dark: blue[300],
        light: '#081437',
        main: blue[400],
      },
      primary: {
        contrastText: zinc[900],
        dark: blue[200],
        light: blue[800],
        main: blue[300],
      },
      secondary: {
        contrastText: zinc[100],
        dark: alpha(zinc[400], 0.1),
        light: alpha(zinc[400], 0.3),
        main: alpha(zinc[400], 0.2),
      },
      success: {
        dark: emerald[300],
        light: '#021F17',
        main: emerald[400],
      },
      text: {
        disabled: zinc[500],
        primary: zinc[300],
        secondary: zinc[400],
      },
      Tooltip: {
        bg: zinc[800],
      },
      warning: {
        dark: orange[200],
        light: '#311104',
        main: orange[300],
      },
    },
  },
};

const darkThemeColorSchemes: Partial<Record<SupportedColorScheme, ColorSystemOptions>> = {
  dark: {
    palette: {
      mode: 'dark',
      action: {
        focusRing: blue[500],
      },
      Alert: {
        errorColor: zinc[300],
        infoColor: zinc[300],
        successColor: zinc[300],
        warningColor: zinc[300],
      },
      background: {
        contrast: slate[200],
        default: common.black,
        input: zinc[900],
        paper: zinc[900],
      },
      border: zinc[600],
      contrastThreshold: 4.5,
      divider: zinc[600],
      error: {
        contrastText: zinc[900],
        dark: red[300],
        light: '#2F0808',
        main: red[400],
      },
      info: {
        dark: blue[300],
        light: '#081437',
        main: blue[400],
      },
      primary: {
        contrastText: zinc[900],
        dark: blue[200],
        light: blue[800],
        main: blue[300],
      },
      secondary: {
        contrastText: zinc[100],
        dark: alpha(zinc[400], 0.1),
        light: alpha(zinc[400], 0.3),
        main: alpha(zinc[400], 0.2),
      },
      success: {
        dark: emerald[300],
        light: '#021F17',
        main: emerald[400],
      },
      text: {
        disabled: zinc[500],
        primary: zinc[300],
        secondary: zinc[400],
      },
      Tooltip: {
        bg: zinc[800],
      },
      warning: {
        dark: orange[200],
        light: '#311104',
        main: orange[300],
      },
    },
  },
};

export const useMuiTheme = () => {
  const isDark = useMediaQuery('(prefers-color-scheme: dark)');
  const { i18n } = useTranslation();
  const direction = i18n.dir(i18n.language);

  const family = useCreation(() => {
    return getFontFamily();
  }, [i18n.language]);

  return {
    isDark,
    theme: createTheme({
      ...baseThemeConfig,
      direction,
      colorSchemes: isDark ? darkThemeColorSchemes : lightThemeColorSchemes,
      typography: {
        ...baseThemeConfig.typography,
        fontFamily: family,
      },
    }),
  };
};
